import { Pipe, PipeTransform } from '@angular/core';
import { StudyFile } from 'src/app/models/Study/study-file.model';
import { MediaCategory } from 'src/app/models/media/media-category.model';

interface GroupedResult {
  shortName: string;
  isPrimaryColor: boolean;
  isOptional: boolean;
  leftHandSideStudyFiles: StudyFile[];
  rightHandSideStudyFiles: StudyFile[];
}

interface TempResult extends GroupedResult {
  sortOrder: number;
}

@Pipe({
  name: 'withStudyFile',
  pure: false,
})
export class WithStudyFilePipe implements PipeTransform {
  transform(
    mediaCategories: any[],
    studyFiles: StudyFile[],
    showAllStudies: boolean = false,
    hideCategoriesWithStudies: boolean = false
  ): GroupedResult[] {
    

    const groups = new Map<string, { left: StudyFile[], right: StudyFile[], sortOrder: number, isPrimaryColor: boolean, isOptional: boolean }>();

    const distinctShortNames = Array.from(new Set(mediaCategories.map(item => item.shortName).filter(Boolean)));

    for (const shortName of distinctShortNames) {
     
      const categories = mediaCategories.filter(x => x.shortName === shortName);
      if (categories.length === 0) continue;

      const group = groups.get(shortName) ?? { left: [], right: [], sortOrder: null, isPrimaryColor: true, isOptional: false };
      groups.set(shortName, group);

      for (const category of categories) {
        if (!group.sortOrder) {
          group.sortOrder = category.sortOrder;
        }

        if (!group.isOptional && !category.isRequired) {
          group.isOptional = true;
        }
        const side = category.leftOrRight || '';
        if (side.includes('Left')) {
          if (category.studyFiles.length > 0) {
            group.left.push(...category.studyFiles);
          } else {
            group.left.push({ mediaCategoryId: category.id } as StudyFile);
            if (group.isPrimaryColor && category.isRequired) {
              group.isPrimaryColor = false;
            }
          }
        }
        if (side.includes('Right')) {
          if (category.studyFiles.length > 0) {
            group.right.push(...category.studyFiles);
          } else {
            group.right.push({ mediaCategoryId: category.id } as StudyFile);
            if (group.isPrimaryColor && category.isRequired) {
              group.isPrimaryColor = false;
            }
          }
        }
      }

      // Final fallback: If left or right is still empty, find any matching category by side
      if (group.left.length === 0) {
        const leftCategory = categories.find(c => (c.leftOrRight || '').includes('Left'));
        if (leftCategory) {
          group.left.push({ mediaCategoryId: leftCategory.id } as StudyFile);
          if (group.isPrimaryColor && leftCategory.isRequired) {
            group.isPrimaryColor = false;
          }
        }
      }
      if (group.right.length === 0) {
        const rightCategory = categories.find(c => (c.leftOrRight || '').includes('Right'));
        if (rightCategory) {
          group.right.push({ mediaCategoryId: rightCategory.id } as StudyFile);
          if (group.isPrimaryColor && rightCategory.isRequired) {
            group.isPrimaryColor = false;
          }
        }
      }
    }

    let tempResult: TempResult[] = Array.from(groups.entries()).map(([shortName, { left, right, sortOrder, isPrimaryColor, isOptional }]) => ({
      shortName,
      leftHandSideStudyFiles: left,
      rightHandSideStudyFiles: right,
      sortOrder,
      isPrimaryColor,
      isOptional
    }));

    tempResult.sort((a, b) => a.sortOrder - b.sortOrder);

    let result: GroupedResult[] = tempResult.map(({ sortOrder, ...rest }) => rest);

    const hasStudyFiles = (g: GroupedResult) => g.leftHandSideStudyFiles.length > 0 || g.rightHandSideStudyFiles.length > 0;

    if (showAllStudies) {
      return result.filter(hasStudyFiles);
    }

    if (hideCategoriesWithStudies) {
      // Assuming based on name: hide categories with studies, so return those without? But name is hide categories with studies, so filter !has
      return result.filter(g => !hasStudyFiles(g));
    }

    return result;
  }
}
